/**
 * useCurrentUser - Provides current user info for collaboration
 *
 * Returns user data needed for awareness state:
 * - id: User ID from auth
 * - name: First name for cursor label
 * - color: Assigned cursor color (deterministic from user ID)
 *
 * Uses the auth state from React Query (set up in Phase 02-06).
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { PresenceUser } from '@deep-agents/shared-types';
import { CURSOR_COLORS } from '@deep-agents/shared-types';

// Fetch current user from /api/auth/me
async function fetchCurrentUser() {
  const response = await fetch('/api/auth/me', {
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error('Not authenticated');
  }

  const data = await response.json();
  // API returns { data: { user: { id, email, ... } } }
  return data.data?.user;
}

interface CurrentUserResult {
  // User data for awareness
  user: PresenceUser | null;
  // Loading state
  isLoading: boolean;
  // Error state
  error: Error | null;
}

export function useCurrentUser(): CurrentUserResult {
  const { data, isLoading, error } = useQuery({
    queryKey: ['currentUser'],
    queryFn: fetchCurrentUser,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: false,
  });

  // Convert auth user to PresenceUser for awareness
  const user = useMemo((): PresenceUser | null => {
    // Defensive check: ensure data and required fields exist
    if (!data || !data.id) return null;

    // Deterministic color from user ID
    const colorIndex = hashCode(data.id) % CURSOR_COLORS.length;

    return {
      id: data.id,
      name: getFirstName(data.displayName || data.email || 'User'),
      color: CURSOR_COLORS[colorIndex],
      avatarUrl: data.avatarUrl,
    };
  }, [data]);

  return {
    user,
    isLoading,
    error: error as Error | null,
  };
}

/**
 * Simple string hash for deterministic color assignment
 */
function hashCode(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash);
}

/**
 * Extract first name from display name or email
 */
function getFirstName(nameOrEmail: string): string {
  // If it's an email, use part before @
  if (nameOrEmail.includes('@')) {
    return nameOrEmail.split('@')[0];
  }
  // Otherwise, use first word
  return nameOrEmail.split(' ')[0];
}
